import { TranslateService } from '@ngx-translate/core'
import _ from 'lodash'
import { OperatorFunction } from 'rxjs'
import { map } from 'rxjs/operators'
export async function retrieveAndSetLanguage(
  langCode: string,
  translate: TranslateService,
  overrideValue,
  suffix = '.json'
) {
  if (langCode)
    import(`../../../assets/i18n/${langCode}${suffix}`).then(res => {
      const newValue = _.merge({}, res.default, overrideValue)
      translate.setTranslation(langCode, newValue)
    })
}

const LANGUAGE_NATIVE_NAME_MAP: { [key: string]: string } = {
  'en-US': 'English',
  'ar-SA': 'العربية',
  'de-DE': 'Deutsch',
  'fr-FR': 'Français',
  'hi-IN': 'हिन्दी',
  'zh-CN': '中文',
  'es-ES': 'Español',
  'es-MX': 'Español (México)',
  'it-IT': 'Italiano',
  'ja-JP': '日本語',
  'ko-KR': '한국어',
  'pt-PT': 'Português',
  ms: 'Bahasa Melayu',
  'ro-RO': 'Română',
  'si-LK': 'සිංහල',
  'tl-PH': 'Filipino',
  'bn-IN': 'বাংলা',
  'sw-KE': 'Kiswahili',
  'nl-NL': 'Dutch',
  'th-TH': 'ไทย'
}

export function getLanguageNativeName(language: { bcp47Code?: string; name?: string }): string {
  if (!language) return ''
  return LANGUAGE_NATIVE_NAME_MAP[language.bcp47Code] || language.name || ''
}

// bcp47 codes that exist as legacy languageMaster rows but are not part of the approved language list (KM-5154)
export const UNSUPPORTED_LANGUAGE_CODES = ['en-IN']

// names are matched too because the same legacy row is keyed by name in knowmax-fe and may be renamed per tenant
export const UNSUPPORTED_LANGUAGE_NAMES = ['english-indo']

export function isSupportedLanguage(language: { bcp47Code?: string; name?: string }): boolean {
  if (!language) return false
  if (UNSUPPORTED_LANGUAGE_CODES.includes(language.bcp47Code)) return false
  return !UNSUPPORTED_LANGUAGE_NAMES.includes((language.name || '').trim().toLowerCase())
}

export function filterSupportedLanguages(languages: any[]): any[] {
  return Array.isArray(languages) ? languages.filter(language => isSupportedLanguage(language)) : languages
}

// which label a screen actually renders, so each list can be sorted by the text the user reads
export type LanguageLabelKey = 'api' | 'native'

// display order: English first, then Arabic, then alphabetical by the rendered label.
// 'api' is the English name the API returns (German), 'native' is what the header renders (Deutsch).
// knowmax-fe sorts its header on the native name, so 'native' keeps the two products identical there.
export function sortLanguagesForDisplay(languages: any[], labelKey: LanguageLabelKey = 'api'): any[] {
  if (!Array.isArray(languages)) return languages
  // matched on bcp47Code and name for the same reason the deny-list is: the two repos key on different fields
  const rank = (language: { bcp47Code?: string; name?: string }) =>
    language?.bcp47Code === 'en-US' || language?.name === 'English'
      ? 0
      : language?.bcp47Code === 'ar-SA' || language?.name === 'Arabic'
      ? 1
      : 2
  const label = (language: { bcp47Code?: string; name?: string }) =>
    labelKey === 'native' ? getLanguageNativeName(language) : language?.name || ''
  return [...languages].sort((a, b) => rank(a) - rank(b) || label(a).localeCompare(label(b)))
}

// pipe this onto every languages API call. Sorts on the API name because that is what most admin dropdowns
// render; the three screens that render native labels re-sort with 'native' after subscribing.
export function supportedLanguagesOnly(): OperatorFunction<any[], any[]> {
  return map((languages: any[]) => sortLanguagesForDisplay(filterSupportedLanguages(languages)))
}
